import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { downloadCsvMatrix, type CsvCell } from "@/lib/csv";
import { formatFullDate, formatRelativeTime } from "@/lib/format";
import type { RedemptionBatch, RedemptionExportLog } from "@/pages/licenses/hooks/useRedemptionBatches";

interface RedemptionBatchBreakdownProps {
  batches: RedemptionBatch[];
  exportLogs?: RedemptionExportLog[];
  total: number;
  loading: boolean;
  error: string | null;
  onLogExport?: (batchId: string, count: number, kind?: string) => Promise<void>;
}

// 「激活码核销占比」卡下钻：把核销来的许可证按批次拆分，看每个批次各贡献了多少张。
export default function RedemptionBatchBreakdown({
  batches,
  exportLogs,
  total,
  loading,
  error,
  onLogExport,
}: RedemptionBatchBreakdownProps) {
  const { t } = useTranslation();
  type SortKey = "count" | "recent";
  const [sortBy, setSortBy] = useState<SortKey>("count");
  const [productFilter, setProductFilter] = useState<string>("all");
  const [copiedBatch, setCopiedBatch] = useState<string | null>(null);
  const [downloadedBatch, setDownloadedBatch] = useState<string | null>(null);

  const sortOptions: { key: SortKey; label: string; icon: string; title: string }[] = [
    { key: "count", label: t("licenses.breakdownSortCount"), icon: "ri-bar-chart-2-line", title: t("licenses.breakdownSortCountTitle") },
    { key: "recent", label: t("licenses.breakdownSortRecent"), icon: "ri-time-line", title: t("licenses.breakdownSortRecentTitle") },
  ];

  const genericProduct = t("licenses.breakdownGenericProduct");

  // 某个批次里最近一次核销的时间：取该批次各证核销时间（issuedAt）里的最大值。
  const latestRedeemedAt = (batch: RedemptionBatch): string | null =>
    (batch.items ?? []).reduce<string | null>((latest, item) => {
      if (!item.issuedAt) return latest;
      if (!latest || item.issuedAt > latest) return item.issuedAt;
      return latest;
    }, null);

  // 把单个批次的核销证整理成可粘贴的制表符分隔清单（含表头），方便存档或做台账。
  const buildBatchListText = (batch: RedemptionBatch): string => {
    const items = batch.items ?? [];
    const header = t("licenses.batchListHeader", { batch: batch.batchId, count: batch.count });
    const columns = [
      t("licenses.colLicenseKey"),
      t("licenses.colProduct"),
      t("licenses.colEdition"),
      t("licenses.colSeats"),
      t("licenses.colRedeemedAt"),
    ].join("\t");
    const lines = items.map((item) =>
      [
        item.licenseKey,
        item.productName || genericProduct,
        item.edition || "standard",
        String(item.seatCount),
        item.issuedAt ? formatFullDate(item.issuedAt) : "-",
      ].join("\t"),
    );
    return `${header}\n${columns}\n${lines.join("\n")}\n`;
  };

  // 正式 CSV 导出：与「未核销激活码清单」下载对称，文件名带批次号与日期，方便按批次归档对账。
  const downloadBatchRedemptionCsv = (batch: RedemptionBatch): number => {
    const items = batch.items ?? [];
    const header = [
      t("licenses.colLicenseKey"),
      t("licenses.colProduct"),
      t("licenses.colEdition"),
      t("licenses.colSeats"),
      t("licenses.colRedeemedAt"),
    ];
    const rows: CsvCell[][] = items.map((item) => [
      item.licenseKey,
      item.productName || genericProduct,
      item.edition || "standard",
      item.seatCount,
      item.issuedAt ? formatFullDate(item.issuedAt) : "",
    ]);
    const stamp = new Date().toISOString().slice(0, 10);
    const safeBatch = (batch.batchId || "batch").replace(/[^\w.-]/g, "_");
    downloadCsvMatrix(`${t("licenses.redeemedFilePrefix")}_${safeBatch}_${stamp}.csv`, [header, ...rows]);
    return items.length;
  };

  const max = batches.reduce((peak, item) => Math.max(peak, item.count), 0);

  // 面板里出现过的产品（去重），多产品场景下按产品只看某个产品的核销贡献。
  const productOptions = useMemo(() => {
    const set = new Set<string>();
    batches.forEach((batch) => set.add(batch.productName || genericProduct));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [batches, genericProduct]);

  // 每个产品累计贡献的核销张数，让筛选条直观看到各产品各贡献了多少张。
  const productCounts = useMemo(() => {
    const map = new Map<string, number>();
    batches.forEach((batch) => {
      const key = batch.productName || genericProduct;
      map.set(key, (map.get(key) ?? 0) + batch.count);
    });
    return map;
  }, [batches, genericProduct]);

  // 先按产品筛选，再排序：默认按贡献张数，切换后可按「最近核销」把最活跃的批次顶到最前。
  const sortedBatches = useMemo(() => {
    const list = productFilter === "all"
      ? [...batches]
      : batches.filter((batch) => (batch.productName || genericProduct) === productFilter);
    if (sortBy === "recent") {
      return list.sort((a, b) => {
        const la = latestRedeemedAt(a);
        const lb = latestRedeemedAt(b);
        if (!la && !lb) return b.count - a.count;
        if (!la) return 1;
        if (!lb) return -1;
        return lb.localeCompare(la);
      });
    }
    return list.sort((a, b) => b.count - a.count);
  }, [batches, productFilter, sortBy, genericProduct]);

  // 当前筛选下的核销张数与批次（未筛选时等于全局）。
  const filteredTotal = sortedBatches.reduce((sum, batch) => sum + batch.count, 0);

  // 每个批次最近一次导出留痕（导出人 + 时间），与后台「未核销清单」留痕共用一套记录。
  const latestExport = useMemo(() => {
    const map = new Map<string, RedemptionExportLog>();
    (exportLogs ?? []).forEach((log) => {
      const prev = map.get(log.batch_id);
      if (!prev || log.created_at > prev.created_at) map.set(log.batch_id, log);
    });
    return map;
  }, [exportLogs]);

  const copyBatch = async (batch: RedemptionBatch) => {
    try {
      await navigator.clipboard.writeText(buildBatchListText(batch));
      setCopiedBatch(batch.batchId);
      window.setTimeout(() => setCopiedBatch((prev) => (prev === batch.batchId ? null : prev)), 2000);
    } catch {
      // 剪贴板不可用时静默失败，不影响查看
    }
  };

  const downloadBatch = async (batch: RedemptionBatch) => {
    const count = downloadBatchRedemptionCsv(batch);
    if (count === 0) return;
    setDownloadedBatch(batch.batchId);
    window.setTimeout(() => setDownloadedBatch((prev) => (prev === batch.batchId ? null : prev)), 2000);
    try {
      await onLogExport?.(batch.batchId, count, "redeem");
    } catch {
      // 留痕写入失败不影响已完成的下载
    }
  };

  return (
    <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50/60 p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-700">
            <i className="ri-stack-line" />
          </span>
          <div>
            <h3 className="font-heading text-sm font-semibold text-foreground-950">{t("licenses.breakdownTitle")}</h3>
            <p className="text-[11px] text-foreground-500">{t("licenses.breakdownSub")}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center gap-1 rounded-full border border-background-200 bg-background-50 px-1 py-1">
            {sortOptions.map((option) => {
              const active = sortBy === option.key;
              return (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setSortBy(option.key)}
                  aria-pressed={active}
                  title={option.title}
                  className={`inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${
                    active
                      ? "bg-accent-500 text-background-50"
                      : "text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  <span className="flex h-3 w-3 items-center justify-center">
                    <i className={option.icon} />
                  </span>
                  {option.label}
                </button>
              );
            })}
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50 px-3 py-1 text-[11px] font-medium text-accent-800">
            <span className="flex h-3 w-3 items-center justify-center">
              <i className="ri-gift-2-line" />
            </span>
            {t("licenses.breakdownTotal", {
              count: productFilter === "all" ? total : filteredTotal,
              batches: sortedBatches.length,
            })}
          </span>
        </div>
      </div>

      {productOptions.length > 1 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="flex items-center gap-1.5 whitespace-nowrap text-[11px] text-foreground-500">
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-filter-3-line" />
            </span>
            {t("licenses.breakdownProduct")}
          </span>
          <button
            type="button"
            onClick={() => setProductFilter("all")}
            className={`inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${
              productFilter === "all"
                ? "bg-accent-500 text-background-50"
                : "border border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
            }`}
          >
            {t("licenses.breakdownAllProducts")}
            <span className={productFilter === "all" ? "text-background-200" : "text-foreground-400"}>{total}</span>
          </button>
          {productOptions.map((name) => {
            const active = productFilter === name;
            return (
              <button
                key={name}
                type="button"
                onClick={() => setProductFilter(name)}
                title={t("licenses.breakdownFilterByProduct", { name })}
                className={`inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${
                  active
                    ? "bg-accent-500 text-background-50"
                    : "border border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
                }`}
              >
                {name}
                <span className={active ? "text-background-200" : "text-foreground-400"}>
                  {productCounts.get(name) ?? 0}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <p className="mt-4 text-xs text-foreground-500">{t("licenses.breakdownLoading")}</p>
      ) : error ? (
        <p className="mt-4 flex items-center gap-1.5 text-xs text-accent-800">
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className="ri-error-warning-line" />
          </span>
          {error}
        </p>
      ) : batches.length === 0 ? (
        <p className="mt-4 text-xs text-foreground-500">{t("licenses.breakdownEmpty")}</p>
      ) : (
        <ul className="mt-4 flex flex-col gap-2.5">
          {sortedBatches.map((batch) => {
            const lastRedeemed = latestRedeemedAt(batch);
            const exportInfo = latestExport.get(batch.batchId);
            return (
              <li key={batch.batchId} className="rounded-md border border-background-200 bg-background-50 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex min-w-0 flex-col">
                    <span className="font-mono text-xs text-foreground-900">{batch.batchId}</span>
                    <span className="text-[11px] text-foreground-500">{batch.productName || genericProduct}</span>
                    <span className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-foreground-500">
                      <span className="flex h-3 w-3 items-center justify-center">
                        <i className="ri-time-line" />
                      </span>
                      {t("licenses.breakdownLastRedeemed", {
                        time: lastRedeemed ? formatRelativeTime(lastRedeemed) : t("licenses.breakdownNeverRedeemed"),
                      })}
                    </span>
                    {exportInfo && (
                      <span className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-foreground-400">
                        <span className="flex h-3 w-3 items-center justify-center">
                          <i className="ri-history-line" />
                        </span>
                        {t("licenses.breakdownLastExport", {
                          who: exportInfo.exported_by_name || "—",
                          date: formatFullDate(exportInfo.created_at),
                        })}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => void copyBatch(batch)}
                      title={t("licenses.breakdownCopyTitle")}
                      className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 py-1 text-[11px] font-medium text-foreground-700 transition-colors hover:bg-background-100"
                    >
                      <span className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className={copiedBatch === batch.batchId ? "ri-checkbox-circle-line" : "ri-file-copy-line"} />
                      </span>
                      {copiedBatch === batch.batchId ? t("licenses.breakdownCopied") : t("licenses.breakdownCopy")}
                    </button>
                    <button
                      type="button"
                      onClick={() => void downloadBatch(batch)}
                      title={t("licenses.breakdownDownloadTitle")}
                      className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 py-1 text-[11px] font-medium text-foreground-700 transition-colors hover:bg-background-100"
                    >
                      <span className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className={downloadedBatch === batch.batchId ? "ri-checkbox-circle-line" : "ri-download-2-line"} />
                      </span>
                      {downloadedBatch === batch.batchId ? t("licenses.breakdownDownloaded") : t("licenses.breakdownDownload")}
                    </button>
                    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-accent-100 px-2.5 py-0.5 text-[11px] font-medium text-accent-800">
                      <span className="flex h-3 w-3 items-center justify-center">
                        <i className="ri-key-2-line" />
                      </span>
                      {t("licenses.breakdownCountUnit", { count: batch.count })}
                    </span>
                  </div>
                </div>
                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-background-200">
                  <div
                    className="h-full rounded-full bg-accent-500"
                    style={{ width: `${max > 0 ? Math.round((batch.count / max) * 100) : 0}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}